import { browserHistory } from 'react-router';
import { delay, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import * as apiUtils from '../../services/apiUtils';
import * as api from '../../services/customer/api_customer';
import * as apiCommon from '../../services/apiCommon';
import * as common from '../sagasCommon';
import * as utilityPreference from '../../services/preference/utilty_preference';
import * as sectionTableActions from '../../components/common/form/SectionTable/actions';
import { ENTITY, ENTITY_TYPE } from '../../constants/constant_doc_types';
import { CUSTOMER } from '../../constants/constant_entity_types';
import { getParticularPreferenceSettings } from '../../common/selectors/preference';
import { getErrorMessage } from '../../utils';
import { precheckEntity } from '../sagasCommonEntity';

export function* fetchListCustomerSaga(feathersApp) {
  yield takeLatest('FETCH_LIST_CUSTOMERS', callListCustomer, feathersApp);
}
function* callListCustomer(feathersApp, action) {
  const { filters = {}, silent } = action;
  yield common.showProgressListTable(silent);
  try {
    const dataParams = {
      filters,
      doc_type: 'entity/customer'
    };
    const tableData = yield call(apiCommon.getDocs, feathersApp, dataParams);
    yield call(common.doneListTable, {
      rows: tableData.data,
      total_rows: tableData.total
    });
  } catch (error) {
    yield common.showErrorListTable(error);
  }
}

export function* deleteCustomerFromListSaga(feathersApp) {
  yield takeEvery('DELETE_CUSTOMER_FROM_LIST', callDeleteCustomerFromList, feathersApp);
}

function* callDeleteCustomerFromList(feathersApp, action) {
  yield deleteCustomer(feathersApp, action);
  yield delay(2000);
  yield callListCustomer(feathersApp, { silent: true });
}

function* deleteCustomer(feathersApp, action) {
  yield common.showProgressDeleteRecord();
  try {
    const { id } = action;
    if (id) {
      const state = yield select();
      const { tenant, user } = state;
      const params = { tenant, user, id };
      const result = yield call(api.deleteCustomer, feathersApp, params);
      yield common.doneDeleteRecord(id, ENTITY);
      yield delay(2000);
      yield callListCustomer(feathersApp, { silent: true });
      return result;
    }
    return null;
  } catch (error) {
    yield common.showErrorDeleteRecord(error);
  }
}

export function* deleteCustomerSaga(feathersApp) {
  yield takeEvery('DELETE_CUSTOMER', callDeleteCustomer, feathersApp);
}
function* callDeleteCustomer(feathersApp, action) {
  yield common.showProgressDeleteRecord();
  try {
    const { id, fromList } = action;
    if (id) {
      const state = yield select();
      const { tenant, user } = state;
      const { id } = action;
      const params = { tenant, user, id };
      yield call(api.deleteCustomer, feathersApp, params);
    }
    yield common.doneDeleteRecord(id, ENTITY);
    if (!fromList) {
      browserHistory.push('/admin/customer');
    } else {
      yield callListCustomer({ silent: true });
    }
  } catch (error) {
    yield common.showErrorDeleteRecord(error);
  }
}

export function* fetchCustomerForEditSaga(feathersApp) {
  yield takeEvery('FETCH_CUSTOMER_FOR_EDIT', callFetchCustomerForEdit, feathersApp);
}
function* callFetchCustomerForEdit(feathersApp, action) {
  const { tenant, user, subsidiary, intl, path } = yield select();
  const { id } = action;

  try {
    yield common.showProgressDataById();

    let data;

    if (id) {
      const { value: expiryDays } = yield select(getParticularPreferenceSettings,
        'loyalty_points_expiry',
        ['value']
      );
      const fetchParams = { 
        tenant,
        user,
        id,
        path,
        haveCustomFields: true,
        loyaltyDisplay: 'dt:loyalty_display:points', // use points display
        expiryDays,
      };
      let doc = yield call(api.fetchCustomerForEdit, feathersApp, fetchParams);

      //set payment parameters
      const customerPaymentDataParams = {
        filters: { customer: id },
        query_type: 'customer_payment'
      };
      //call api to get payments
      const customerPaymentData = yield call(
        apiCommon.getCustomerPaymentData,
        feathersApp,
        customerPaymentDataParams
      );
      //then set customer payments
      doc.customer_payments = customerPaymentData && customerPaymentData.data;

      const sortSublists = [{ name: 'address_details' }, { name: 'contacts' }];
      doc = { ...apiUtils.sortSublists(doc, sortSublists) };

      (doc.contact_numbers || []).reverse();
      (doc.address_details || []).reverse();

      doc.custom_fields = yield call(
        apiUtils.attachStandardFieldValuesToCustomForm,
        doc,
        ENTITY,
      );

      data = doc;
    } else {
      const entityTypesParams = { cid: [CUSTOMER] };
      const entityTypes = yield call(
        apiCommon.queryDocs,
        feathersApp,
        ENTITY_TYPE,
        entityTypesParams
      );
      const accountId = (tenant || {}).id;
      const subsidiaryId = subsidiary.primary_subsidiary.id || subsidiary.primary_subsidiary._id;
      const defaultCurrency = subsidiary.primary_subsidiary.currency || {};
      const preferenceParams = {
        accountId,
        subsidiaryId,
        intl,
        settings: ['default_price_level', 'default_entity_loyalty']
      };
      const preferences = yield call(
        utilityPreference.getPreferenceValues,
        feathersApp,
        preferenceParams
      );

      const initialValues = {
        entity_type_ids: entityTypes.data.length ? [entityTypes.data[0].id] : [],
        is_company: false,
        loyalty_anniversary: preferences.default_entity_loyalty
          ? new Date()
          : undefined,
        loyalty_member: preferences.default_entity_loyalty,
        loyalty_points_available: 0,
        contact_numbers: [{ primary: true, type: 'home' }],
        active: true,
        price_level_id: preferences.default_entity_loyalty
          ? preferences.default_price_level
          : undefined,
        preferred_currency_id: defaultCurrency.id,
        preferred_currency: defaultCurrency
      };

      data = initialValues;
    }

    yield common.doneDataById(data);
  } catch (error) {
    yield common.showErrorDataById(error);
  }
}

export function* saveCustomerSaga(feathersApp) {
  yield takeEvery('SAVE_CUSTOMER', callSaveCustomer, feathersApp);
}
function* callSaveCustomer(feathersApp, action) {
  yield common.showProgressSaveRecord();
  const { tenant, user } = yield select();
  try {
    const ok = yield precheckEntity(feathersApp, action);
    if (!ok) {
      yield common.doneSaveRecord();
      return;
    }

    const sortSublists = [{ name: 'address_details' }, { name: 'contacts' }];

    const result = yield call(api.saveCustomer, feathersApp, {
      tenant,
      user,
      data: { ...apiUtils.sortSublists(action.data, sortSublists) },
    });
    const id = result && result.id;
    if (id) {
      browserHistory.push(`/admin/customer/edit/${id}`);
      yield common.doneSaveRecord(result, 'Customer was saved');
    } else {
      yield common.showErrorSaveRecord(new Error('Error in saving : Customer'));
    }
  } catch (error) {
    yield common.showErrorSaveRecord(error);
  }
}

export function* getCustomerSaleTransactions(app) {
  yield takeEvery('GET_CUSTOMER_SALE_TRANSACTIONS', callGetCustomerSaleTransactions, app);
}

function* callGetCustomerSaleTransactions(app, action) {
  const { key, payload } = action;
  yield put(sectionTableActions.setSectionTableLoader(key, true));

  try {
    const { data, total, limit, skip } = yield call(apiCommon.getDocs, app, {
      ...payload,
      filters: {
        ...payload.filters
      },
      list_type: 'sale_transaction'
    });

    const meta = {
      total,
      limit,
      skip
    };

    yield put(sectionTableActions.setSectionTableData(key, { data, meta }));
  } catch (err) {
    yield put(sectionTableActions.setSectionTableError(key, true, getErrorMessage(err)));
  }
}

export function* getCustomerPayments(app) {
  yield takeEvery('GET_CUSTOMER_PAYMENTS', callGetCustomerPayments, app);
}

function* callGetCustomerPayments(app, action) {
  const { key, payload } = action;
  yield put(sectionTableActions.setSectionTableLoader(key, true));

  try {
    const { data, total, limit, skip } = yield call(apiCommon.getDocs, app, {
      ...payload,
      filters: {
        ...payload.filters
      },
      list_type: 'payment'
    });

    const meta = {
      total,
      limit,
      skip
    };

    yield put(sectionTableActions.setSectionTableData(key, { data, meta }));
  } catch (err) {
    yield put(sectionTableActions.setSectionTableError(key, true, getErrorMessage(err)));
  }
}
